import supabase from "../supabaseClient";
import { anonSupabaseRequest, functionsErrorMessage } from "./anonSupabaseRequest";
import { cleanupCompressedVideo, compressVideoForUpload } from "./compressVideoForUpload";
import { STATBOX_CLOUDFRONT_HOST } from "./devMediaProxy";
import {
  MAX_VIDEO_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_GB,
  MULTIPART_CONCURRENCY,
  MULTIPART_PART_SIZE_BYTES,
  S3_BROWSER_MULTIPART_THRESHOLD_BYTES,
  validateVideoFileSize,
} from "./videoUploadLimits";

const S3_BUCKET = "statbox-videos-prod";
const S3_VIRTUAL_HOST = `${S3_BUCKET}.s3.eu-north-1.amazonaws.com`;
const PUT_ATTEMPTS = 5;
const NETWORK_UPLOAD_ERROR =
  "Network error during upload. Keep this tab open — we retry each chunk automatically.";
const ABORTED_ERROR = "Upload was aborted";
const MPU_RESUME_PREFIX = "statbox-s3-mpu:";

/** True when URL should use the HTML5 <video> player (incl. CloudFront full_match_mp4/*_mp4). */
export function isUploadedMp4SourceUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (/^https?:\/\/(www\.)?vimeo\.com\/(\d+)/.test(url)) return false;
  if (/\.(mp4|webm|ogg|mov|avi|m4v)(\?|$)/i.test(url)) return true;
  if (url.includes("file.mp4")) return true;
  if (/\/full_match_mp4\//i.test(url)) return true;
  if (/_mp4(\?|$)/i.test(url)) return true;
  return false;
}

/** Public playback URL for objects under full_match_mp4/ (CloudFront in front of S3). */
export function buildFullMatchCloudFrontUrl(filePath) {
  const key = String(filePath || "").replace(/^\/+/, "");
  if (!key) return null;
  const baseOverride = import.meta.env.VITE_FULL_MATCH_CDN_BASE?.trim();
  const base = (baseOverride || `https://${STATBOX_CLOUDFRONT_HOST}`).replace(/\/$/, "");
  return `${base}/${key}`;
}

function resolvePutUrl(uploadUrl) {
  if (!import.meta.env.DEV) return uploadUrl;
  try {
    const u = new URL(uploadUrl);
    if (u.hostname === S3_VIRTUAL_HOST) {
      return `${window.location.origin}/__s3_proxy${u.pathname}${u.search}`;
    }
  } catch {
    /* keep uploadUrl */
  }
  return uploadUrl;
}

function buildUploadResult(returnedPath, publicUrlFromFunction) {
  const awsRegion = import.meta.env.VITE_AWS_REGION || "eu-north-1";
  const s3PublicUrl = `https://${S3_BUCKET}.s3.${awsRegion}.amazonaws.com/${returnedPath}`;
  const cloudFrontUrl = buildFullMatchCloudFrontUrl(returnedPath);
  const publicUrl = cloudFrontUrl || publicUrlFromFunction || s3PublicUrl;

  return {
    videoUrl: publicUrl,
    publicUrl,
    filePath: returnedPath,
    s3PublicUrl,
    cloudFrontUrl: cloudFrontUrl || null,
  };
}

async function invokeS3PresignedUpload(body) {
  if (body?.allowAnonymous) {
    const data = await anonSupabaseRequest("/functions/v1/s3-presigned-upload", { body });
    if (data?.ok === false) {
      throw new Error(data.error || data.message || "Failed to get S3 upload URL");
    }
    return data;
  }

  const { data, error } = await supabase.functions.invoke("s3-presigned-upload", { body });
  if (error) {
    throw new Error(await functionsErrorMessage(error));
  }
  if (data?.ok === false) {
    throw new Error(data.error || data.message || "Failed to get S3 upload URL");
  }
  return data;
}

function parseS3ErrorMessage(responseText) {
  if (!responseText) return null;
  const codeMatch = responseText.match(/<Code>([^<]+)<\/Code>/);
  const messageMatch = responseText.match(/<Message>([^<]+)<\/Message>/);
  if (codeMatch || messageMatch) {
    return [codeMatch?.[1], messageMatch?.[1]].filter(Boolean).join(": ");
  }
  try {
    const errorData = JSON.parse(responseText);
    return errorData.message || errorData.error || null;
  } catch {
    return responseText.slice(0, 300);
  }
}

function isAbortedError(err) {
  return String(err?.message || "") === ABORTED_ERROR;
}

function isRetryableUploadError(err) {
  if (isAbortedError(err)) return false;
  const msg = String(err?.message || "");
  return /network error|timed out|failed with status 5\d\d|etag header was missing/i.test(msg);
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new Error(ABORTED_ERROR);
  }
}

function notifyProgress(onProgress, loaded, total, extra = {}) {
  if (!onProgress) return;
  const safeTotal = total || 0;
  const percent = safeTotal ? Math.min(99, Math.round((loaded / safeTotal) * 100)) : 0;
  onProgress(percent, { loaded, total: safeTotal, percent, ...extra });
}

function fileFingerprint(file) {
  return `${file?.name || "file"}:${file?.size || 0}:${file?.lastModified || 0}`;
}

function resumeStorageKey(file) {
  return `${MPU_RESUME_PREFIX}${fileFingerprint(file)}`;
}

function loadResumeState(file) {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(resumeStorageKey(file));
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (!state?.uploadId || !state?.filePath || state.fileSize !== file.size) return null;
    return state;
  } catch {
    return null;
  }
}

function saveResumeState(file, state) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(resumeStorageKey(file), JSON.stringify(state));
  } catch {
    /* quota — resume still works in-memory for this session */
  }
}

export function clearS3ResumeState(file) {
  if (!file || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(resumeStorageKey(file));
  } catch {
    /* ignore */
  }
}

async function withPutRetries(run, signal) {
  let lastErr;
  for (let attempt = 1; attempt <= PUT_ATTEMPTS; attempt += 1) {
    throwIfAborted(signal);
    try {
      return await run();
    } catch (err) {
      lastErr = err;
      if (isAbortedError(err) || !isRetryableUploadError(err) || attempt === PUT_ATTEMPTS) {
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** (attempt - 1)));
    }
  }
  throw lastErr;
}

function putOnce(putUrl, body, contentType, { onByteProgress, requireEtag, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = false;
    xhr.timeout = 0;

    const onAbort = () => {
      xhr.abort();
    };

    if (signal) {
      if (signal.aborted) {
        reject(new Error(ABORTED_ERROR));
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }

    const cleanup = () => {
      if (signal) signal.removeEventListener("abort", onAbort);
    };

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onByteProgress) {
        onByteProgress(e.loaded, e.total);
      }
    });

    xhr.addEventListener("load", () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        if (!requireEtag) {
          resolve(null);
          return;
        }
        const etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
        if (!etag) {
          reject(new Error("Upload succeeded but ETag header was missing"));
          return;
        }
        resolve(etag.replace(/^"|"$/g, ""));
        return;
      }
      const detail = parseS3ErrorMessage(xhr.responseText);
      reject(new Error(detail || `Upload failed with status ${xhr.status}`));
    });

    xhr.addEventListener("error", () => {
      cleanup();
      reject(new Error(NETWORK_UPLOAD_ERROR));
    });
    xhr.addEventListener("abort", () => {
      cleanup();
      reject(new Error(ABORTED_ERROR));
    });
    xhr.addEventListener("timeout", () => {
      cleanup();
      reject(new Error(NETWORK_UPLOAD_ERROR));
    });

    xhr.open("PUT", putUrl, true);
    xhr.setRequestHeader("Content-Type", contentType || "video/mp4");
    xhr.send(body);
  });
}

function putBlobWithProgress(putUrl, blob, contentType, onPartProgress, signal) {
  return withPutRetries(
    () =>
      putOnce(putUrl, blob, contentType, {
        requireEtag: true,
        onByteProgress: onPartProgress,
        signal,
      }),
    signal
  );
}

function putFileWithProgress(putUrl, file, contentType, onProgress, signal) {
  const body = typeof file.slice === "function" ? file.slice(0, file.size) : file;
  return withPutRetries(
    () =>
      putOnce(putUrl, body, contentType, {
        requireEtag: false,
        signal,
        onByteProgress: (loaded, total) => {
          notifyProgress(onProgress, loaded, total);
        },
      }),
    signal
  ).then(() => {
    if (onProgress) onProgress(100, { loaded: file.size, total: file.size, percent: 100 });
  });
}

async function uploadSinglePut(file, presignBody, onProgress, signal) {
  throwIfAborted(signal);
  const signedData = await invokeS3PresignedUpload(presignBody);
  const uploadUrl = signedData?.uploadUrl || signedData?.url || signedData?.presignedUrl;
  const returnedPath = signedData?.filePath || signedData?.key || signedData?.objectKey || presignBody.filePath;
  const publicUrlFromFunction = signedData?.publicUrl || signedData?.videoUrl || null;

  if (!uploadUrl) {
    throw new Error("Presigned upload URL missing from s3-presigned-upload response");
  }

  await putFileWithProgress(resolvePutUrl(uploadUrl), file, presignBody.mimeType, onProgress, signal);
  return buildUploadResult(returnedPath, publicUrlFromFunction);
}

async function abortMultipartUpload(presignBody, returnedPath, uploadId) {
  try {
    await invokeS3PresignedUpload({
      action: "abortMultipart",
      bucket: presignBody.bucket,
      filePath: returnedPath,
      uploadId,
      allowAnonymous: presignBody.allowAnonymous,
      uploadToken: presignBody.uploadToken || null,
    });
  } catch {
    /* best-effort cleanup */
  }
}

async function uploadMultipart(file, presignBody, onProgress, signal) {
  const partSize = MULTIPART_PART_SIZE_BYTES;
  const partCount = Math.ceil(file.size / partSize);
  let uploadId;
  let returnedPath;
  let publicUrlFromFunction = null;
  const partsByNumber = new Map();

  const resume = loadResumeState(file);
  if (resume?.uploadId && resume?.filePath) {
    uploadId = resume.uploadId;
    returnedPath = resume.filePath;
    publicUrlFromFunction = resume.publicUrl || null;
    (resume.parts || []).forEach((part) => {
      if (part?.PartNumber && part?.ETag) {
        partsByNumber.set(part.PartNumber, { PartNumber: part.PartNumber, ETag: part.ETag });
      }
    });
  } else {
    const initiateData = await invokeS3PresignedUpload({
      ...presignBody,
      action: "initiateMultipart",
    });
    uploadId = initiateData?.uploadId;
    returnedPath = initiateData?.filePath || initiateData?.key || initiateData?.objectKey || presignBody.filePath;
    publicUrlFromFunction = initiateData?.publicUrl || initiateData?.videoUrl || null;
    if (!uploadId) {
      throw new Error("Multipart uploadId missing from s3-presigned-upload response");
    }
    saveResumeState(file, {
      uploadId,
      filePath: returnedPath,
      fileSize: file.size,
      fileName: file.name,
      publicUrl: publicUrlFromFunction,
      parts: [],
    });
  }

  const partByteSize = (partNumber) => {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    return Math.max(0, end - start);
  };

  let committedBytes = 0;
  partsByNumber.forEach((_, partNumber) => {
    committedBytes += partByteSize(partNumber);
  });

  const pending = [];
  for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
    if (!partsByNumber.has(partNumber)) pending.push(partNumber);
  }

  const inflightLoaded = {};
  const report = () => {
    const inflight = Object.values(inflightLoaded).reduce((sum, n) => sum + n, 0);
    notifyProgress(onProgress, committedBytes + inflight, file.size);
  };
  report();

  let cursor = 0;
  const persist = () => {
    saveResumeState(file, {
      uploadId,
      filePath: returnedPath,
      fileSize: file.size,
      fileName: file.name,
      publicUrl: publicUrlFromFunction,
      parts: Array.from(partsByNumber.values()),
    });
  };

  const uploadOnePart = async (partNumber) => {
    throwIfAborted(signal);
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    const signData = await invokeS3PresignedUpload({
      action: "signPart",
      bucket: presignBody.bucket,
      filePath: returnedPath,
      uploadId,
      partNumber,
      allowAnonymous: presignBody.allowAnonymous,
      uploadToken: presignBody.uploadToken || null,
    });

    const partUrl = signData?.uploadUrl || signData?.url || signData?.presignedUrl;
    if (!partUrl) {
      throw new Error(`Presigned URL missing for multipart part ${partNumber}`);
    }

    const etag = await putBlobWithProgress(
      resolvePutUrl(partUrl),
      blob,
      presignBody.mimeType,
      (loaded) => {
        inflightLoaded[partNumber] = loaded;
        report();
      },
      signal
    );

    delete inflightLoaded[partNumber];
    committedBytes += blob.size;
    partsByNumber.set(partNumber, { PartNumber: partNumber, ETag: etag });
    persist();
    report();
  };

  const worker = async () => {
    while (true) {
      throwIfAborted(signal);
      const index = cursor;
      cursor += 1;
      if (index >= pending.length) return;
      await uploadOnePart(pending[index]);
    }
  };

  try {
    const workerCount = Math.min(MULTIPART_CONCURRENCY, Math.max(1, pending.length));
    if (pending.length > 0) {
      await Promise.all(Array.from({ length: workerCount }, () => worker()));
    }

    const parts = [];
    for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
      const part = partsByNumber.get(partNumber);
      if (!part) {
        throw new Error(`Missing ETag for multipart part ${partNumber}`);
      }
      parts.push(part);
    }

    const completeData = await invokeS3PresignedUpload({
      action: "completeMultipart",
      bucket: presignBody.bucket,
      filePath: returnedPath,
      uploadId,
      parts,
      allowAnonymous: presignBody.allowAnonymous,
      uploadToken: presignBody.uploadToken || null,
    });

    clearS3ResumeState(file);
    if (onProgress) onProgress(100, { loaded: file.size, total: file.size, percent: 100 });

    const finalPath = completeData?.filePath || completeData?.key || completeData?.objectKey || returnedPath;
    const finalPublicUrl = completeData?.publicUrl || completeData?.videoUrl || publicUrlFromFunction;
    return buildUploadResult(finalPath, finalPublicUrl);
  } catch (err) {
    if (isAbortedError(err)) {
      clearS3ResumeState(file);
      await abortMultipartUpload(presignBody, returnedPath, uploadId);
    }
    throw err;
  }
}

function mapUploadProgress(onProgress, compressed, originalSize) {
  if (!onProgress) return undefined;
  if (!compressed) {
    return (percent, extra = {}) => {
      onProgress(percent, { phase: "uploading", ...extra });
    };
  }
  return (percent, extra = {}) => {
    const overall = percent >= 100 ? 100 : Math.min(99, Math.round(30 + (percent || 0) * 0.7));
    onProgress(overall, {
      phase: "uploading",
      phasePercent: percent,
      originalSize,
      ...extra,
    });
  };
}

/**
 * Upload video file to AWS S3 via presigned URL edge function.
 * Browser uploads above 256 MB use multipart so Safari/flaky connections don't drop a single huge PUT.
 * Completed parts are stored in sessionStorage so a retry of the same file resumes instead of restarting.
 * Large videos are compressed in the browser first (1080p H.264) to cut upload time.
 */
/**
 * Upload a local repaired MP4 by HTTP range, without loading the whole file into memory.
 * Stream copy only: the caller must not re-encode before this.
 */
export async function uploadRangeSourceToS3({ url, matchId, onProgress, signal } = {}) {
  if (!url) throw new Error("No repaired file to upload.");
  throwIfAborted(signal);

  const probe = await fetch(url, { headers: { Range: "bytes=0-0" }, signal });
  const sizeMatch = /\/(\d+)\s*$/.exec(probe.headers.get("content-range") || "");
  try { probe.body?.cancel(); } catch { /* ignore */ }
  if (!sizeMatch) throw new Error("Could not read the repaired file size.");
  const size = Number(sizeMatch[1]);
  if (!Number.isFinite(size) || size <= 0) throw new Error("Could not read the repaired file size.");
  if (size > MAX_VIDEO_UPLOAD_BYTES) {
    throw new Error(`File is too large. Maximum file size is ${MAX_VIDEO_UPLOAD_GB}GB.`);
  }

  const readPart = async (start, end) => {
    throwIfAborted(signal);
    const response = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, signal });
    if (!(response.ok || response.status === 206)) {
      throw new Error(`Could not read the repaired file (HTTP ${response.status}).`);
    }
    return response.blob();
  };

  const timestamp = Date.now();
  const fileName = `${matchId || "upload"}_${timestamp}.mp4`;
  const filePath = `full_match_mp4/${matchId || "uploads"}/${fileName}`;
  const presignBody = {
    fileName,
    mimeType: "video/mp4",
    fileSize: size,
    filePath,
    prefix: "full_match_mp4",
    bucket: S3_BUCKET,
    matchId: matchId || null,
    allowAnonymous: false,
  };

  const partSize = MULTIPART_PART_SIZE_BYTES;
  const partCount = Math.ceil(size / partSize);
  const initiateData = await invokeS3PresignedUpload({ ...presignBody, action: "initiateMultipart" });
  const uploadId = initiateData?.uploadId;
  const returnedPath = initiateData?.filePath || initiateData?.key || initiateData?.objectKey || filePath;
  if (!uploadId) throw new Error("Multipart uploadId missing from s3-presigned-upload response");

  let committedBytes = 0;
  const partsByNumber = new Map();
  const pending = Array.from({ length: partCount }, (_, index) => index + 1);
  const inflightLoaded = {};
  const report = () => {
    const inflight = Object.values(inflightLoaded).reduce((sum, n) => sum + n, 0);
    notifyProgress(onProgress, committedBytes + inflight, size, { phase: "uploading" });
  };
  report();

  let cursor = 0;
  const uploadOnePart = async (partNumber) => {
    throwIfAborted(signal);
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, size) - 1;
    const blob = await readPart(start, end);
    const signData = await invokeS3PresignedUpload({
      action: "signPart",
      bucket: presignBody.bucket,
      filePath: returnedPath,
      uploadId,
      partNumber,
    });
    const partUrl = signData?.uploadUrl || signData?.url || signData?.presignedUrl;
    if (!partUrl) throw new Error(`Presigned URL missing for multipart part ${partNumber}`);
    const etag = await putBlobWithProgress(
      resolvePutUrl(partUrl),
      blob,
      presignBody.mimeType,
      (loaded) => {
        inflightLoaded[partNumber] = loaded;
        report();
      },
      signal,
    );
    delete inflightLoaded[partNumber];
    committedBytes += blob.size;
    partsByNumber.set(partNumber, { PartNumber: partNumber, ETag: etag });
    report();
  };

  const worker = async () => {
    while (true) {
      throwIfAborted(signal);
      const index = cursor;
      cursor += 1;
      if (index >= pending.length) return;
      await uploadOnePart(pending[index]);
    }
  };

  try {
    const workerCount = Math.min(MULTIPART_CONCURRENCY, Math.max(1, pending.length));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    const parts = [];
    for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
      const part = partsByNumber.get(partNumber);
      if (!part) throw new Error(`Missing ETag for multipart part ${partNumber}`);
      parts.push(part);
    }
    const completeData = await invokeS3PresignedUpload({
      action: "completeMultipart",
      bucket: presignBody.bucket,
      filePath: returnedPath,
      uploadId,
      parts,
    });
    if (onProgress) onProgress(100, { loaded: size, total: size, percent: 100, phase: "uploading" });
    const finalPath = completeData?.filePath || completeData?.key || completeData?.objectKey || returnedPath;
    return buildUploadResult(finalPath, completeData?.publicUrl || completeData?.videoUrl || null);
  } catch (err) {
    if (isAbortedError(err) && uploadId) await abortMultipartUpload(presignBody, returnedPath, uploadId);
    throw err;
  }
}

export const uploadToS3 = async (file, matchId, onProgress, allowAnonymous = false, options = {}) => {
  if (!file) {
    throw new Error("No file provided");
  }

  validateVideoFileSize(file);

  const signal = options?.signal;
  throwIfAborted(signal);

  let prepared = { file, compressed: false, cleanup: null };
  if (!options?.skipCompress) {
    prepared = await compressVideoForUpload(file, {
      signal,
      onProgress: (phasePercent) => {
        if (!onProgress) return;
        const overall = Math.min(29, Math.round((phasePercent || 0) * 0.3));
        onProgress(overall, {
          phase: "compressing",
          phasePercent,
          loaded: 0,
          total: file.size,
          percent: overall,
        });
      },
    });
  }

  const uploadFile = prepared.file || file;
  const uploadProgress = mapUploadProgress(onProgress, prepared.compressed, file.size);

  try {
    const result = await uploadPreparedFileToS3(
      uploadFile,
      matchId,
      uploadProgress,
      allowAnonymous,
      signal,
      options?.filePath,
      options?.uploadToken,
    );
    await cleanupCompressedVideo(prepared, file);
    return result;
  } catch (err) {
    throw err;
  }
}

async function uploadPreparedFileToS3(file, matchId, onProgress, allowAnonymous, signal, filePathOverride, uploadToken) {
  throwIfAborted(signal);

  if (!allowAnonymous) {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const session = sessionData?.session || null;
    if (sessionError || !session) {
      throw new Error("Authentication required. Please log in again.");
    }
  }

  const timestamp = Date.now();
  const fileExtension = file.name.split(".").pop() || "mp4";
  const fileName = `${matchId || "upload"}_${timestamp}.${fileExtension}`;
  const filePath = filePathOverride || `full_match_mp4/${matchId || "uploads"}/${fileName}`;

  const presignBody = {
    fileName,
    mimeType: file.type || "video/mp4",
    fileSize: file.size,
    filePath,
    prefix: "full_match_mp4",
    bucket: S3_BUCKET,
    matchId: matchId || null,
    allowAnonymous: allowAnonymous || false,
    uploadToken: uploadToken || null,
  };

  if (file.size <= S3_BROWSER_MULTIPART_THRESHOLD_BYTES) {
    return uploadSinglePut(file, presignBody, onProgress, signal);
  }

  try {
    return await uploadMultipart(file, presignBody, onProgress, signal);
  } catch (err) {
    const msg = String(err?.message || "");
    if (msg !== ABORTED_ERROR && /NoSuchUpload|not exist|unknown upload/i.test(msg)) {
      clearS3ResumeState(file);
      return uploadMultipart(file, presignBody, onProgress, signal);
    }
    throw err;
  }
}
